// export const compactNumberFormat = (number) => {
//   return new Intl.NumberFormat("en", {
//     notation: "compact",
//     compactDisplay: "short",
//     maximumFractionDigits: 2,
//   }).format(number);
// };

export const compactNumberFormat = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0";

  const absNum = Math.abs(num);
  let formatted;
  let suffix = "";

  const truncate = (number, digits) => {
    const factor = Math.pow(10, digits);
    return Math.floor(number * factor) / factor;
  };

  if (absNum >= 1_000_000_000_000) {
    formatted = truncate(num / 1_000_000_000_000, 2);
    suffix = "T";
  } else if (absNum >= 1_000_000_000) {
    formatted = truncate(num / 1_000_000_000, 2);
    suffix = "B";
  } else if (absNum >= 1_000_000) {
    formatted = truncate(num / 1_000_000, 2);
    suffix = "M";
  } else if (absNum >= 1_000) {
    formatted = truncate(num / 1_000, 2);
    suffix = "K";
  } else {
    formatted = truncate(num, 2).toFixed(2);
  }

  return `${formatted}${suffix}`;
};

// Truncates a number to two decimal places without rounding (e.g., 234.1999 -> 234.19).
export const truncateToTwoDecimals = (num) => {
  const removeComma = parseFloat(String(num).replace(/,/g, "")); // Remove comma from number
  const sanitizedNumber = typeof num === "string" ? removeComma : num; // Check for type

  const roundToTwo = Math.trunc(sanitizedNumber * 100) / 100;

  const [integer, decimal] = String(roundToTwo).split(".");

  const formatWithDecimal = decimal
    ? numberWithComma(roundToTwo)
    : `${numberWithComma(roundToTwo)}.00`;
  return roundToTwo === 0 ? "0.00" : formatWithDecimal;
};

const numberWithComma = (num) => {
  if (num === null) return "";

  const [intPart, decimalPart] = String(num).split(".");

  // Add commas to integer part
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Join back decimal part if it exists
  const formatted =
    decimalPart !== undefined
      ? `${withCommas}.${decimalPart.padEnd(2, "0")}`
      : withCommas;

  return formatted;
};

export const currencyLocales = {
  PHP: "en-PH",
  JPY: "en-US",
  USD: "en-US",
  EUR: "en-US",
  HKD: "en-US",
  CNY: "en-US",
};

export const currencySymbol = {
  PHP: "₱",
  JPY: "¥",
  USD: "$",
  EUR: "€",
  HKD: "HK$",
  CNY: "CN¥",
};

export const currencyList = {
  PHP: "Philippine Peso (Philippines)",
  USD: "US Dollar (United States)",
  CNY: "Chinese Yuan (China)",
  HKD: "Hong Kong Dollar (Hong Kong)",
  EUR: "Euro (European Union)",
  JPY: "Japanese Yen (Japan)",
};

// Get Currency symbol
export const getCurrencySymbol = (currencyData, currencyId) => {
  const currencyName = currencyData.find(
    (item) => item.id === currencyId,
  )?.currency_name;

  return currencySymbol[currencyName || "PHP"];
};

// Sanitize amount input: remove commas, allow only numbers with one decimal, and trim leading zeros.
export const sanitizeAmount = (value) => {
  value = value.replace(/,/g, ""); // Remove comma
  value = value.replace(/[^\d.]/g, ""); // Remove invalid chars

  // Only allow digits and at most one decimal point; reject all other input
  if (!/^\d*\.?\d*$/.test(value)) {
    value = value.slice(0, -1);
  }

  // Remove unnecessary leading zeros
  if (value.startsWith("0") && value.length > 1 && !value.startsWith("0.")) {
    value = value.replace(/^0+/, "");
  }

  return value;
};

// Format number with commas as thousand separators
export const formatNumberWithComma = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const stringValue = value.toString().replace(/,/g, "");

  // Split integer and decimal
  const [integerPart, decimalPart] = stringValue.split(".");

  // Format only integer part
  const formattedInteger = isNaN(integerPart)
    ? ""
    : Number(integerPart).toLocaleString("en-US");

  // Reattach decimal if exists
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

export const isValidDecimal = (value, maxDecimal = 3) => {
  const regex = new RegExp(`^\\d*\\.?\\d{0,${maxDecimal}}$`);
  return regex.test(value);
};

// Remove commas to a string then convert to number
export const parseNumber = (num) =>
  parseFloat(String(num || 0).replace(/,/g, ""));

export const roundToTwo = (num) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const formatSafeDecimalInput = (value) => {
  if (value === undefined || value === null) return "";

  let safeValue = String(value);

  if (safeValue.includes(".")) {
    const parts = safeValue.split(".");
    if (parts[1].length > 2) {
      safeValue = String(roundToTwo(Number(safeValue)));
    }
  }

  return safeValue;
};

// For display number formatter. (Optimized)
const wholeNumberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const twoDecimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fourDecimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const phpCurrencyFormatter = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
});

const currencyFormatterCache = new Map();

export const formatToNumber = (num) => {
  const value = Number(num) || 0;
  return wholeNumberFormatter.format(value);
};

export const formatToTwoDecimal = (num) => {
  const value = Number(num) || 0;
  return twoDecimalFormatter.format(value);
};

// 4 decimals, but trailing "00" is dropped (e.g. 10.5000 -> 10.50)
export const formatToFourDecimal = (num) => {
  const value = Number(num) || 0;
  const formatted = fourDecimalFormatter.format(value);
  return formatted.endsWith("00") ? formatted.slice(0, -2) : formatted;
};

export const formatToPHP = (num) => {
  const value = Number(num) || 0;
  return phpCurrencyFormatter.format(value);
};

export const formatCurrency = (num, currencyName) => {
  const value = Number(num) || 0;
  const code = (currencyName || "PHP").toUpperCase();

  if (!currencyLocales[code]) {
    console.warn(
      `[formatCurrency] Invalid currency code: "${code}". Defaulting to PHP.`,
    );
    return formatCurrency(value, "PHP");
  }

  if (!currencyFormatterCache.has(code)) {
    const locale = currencyLocales[code];

    currencyFormatterCache.set(
      code,
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: code,
      }),
    );
  }

  return currencyFormatterCache.get(code).format(value);
};
